import type { CircleLayerSpecification, Expression, GeoJSONSource, Map as MapboxMap } from 'mapbox-gl'
import { playTokens } from '@/lib/theme/playTokens'

export const ATHLETE_SOURCE_ID = 'lv-athletes-source'
export const ATHLETE_LAYER_ID = 'lv-athletes'

/**
 * Each tracked athlete's newest fix (docs/features/athletes-and-trackers.md, Map): `color` is their race's course
 * colour, `stale` a fix older than the source's freshness, `selected` the athlete whose card is open or who matches a
 * search, `dim` one a search leaves out.
 */
export interface AthletePointProperties {
  id: string
  color: string
  stale: boolean
  selected: boolean
  dim?: boolean
}

export type AthleteOverlayData = GeoJSON.FeatureCollection<GeoJSON.Point, AthletePointProperties>

const stale: Expression = ['get', 'stale']
const selected: Expression = ['get', 'selected']
const dim: Expression = ['==', ['get', 'dim'], true]

/**
 * One WebGL circle per athlete, so thousands cost nothing in the DOM; under the camera spheres, which are DOM markers.
 * A fresh fix is a dot in the race's colour on an ink rim. A stale one dims to a pale disc ringed in that colour: still
 * plain on satellite, and set apart from a fresh dot by more than colour. The selected athlete's dot grows and wears
 * the Play selection pink, as a search's matches do; the dots a search leaves out fade under them.
 */
export function athleteOverlayLayerSpec(): CircleLayerSpecification {
  return {
    id: ATHLETE_LAYER_ID,
    type: 'circle',
    source: ATHLETE_SOURCE_ID,
    slot: 'top',
    layout: { 'circle-sort-key': ['case', selected, 3, dim, 0, stale, 1, 2] },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, ['case', selected, 7, 5], 14, ['case', selected, 11, 8]],
      'circle-color': ['get', 'color'],
      'circle-opacity': ['case', selected, 1, dim, 0.15, stale, 0.45, 1],
      'circle-stroke-color': ['case', selected, playTokens.color.selected, stale, ['get', 'color'], playTokens.color.ink],
      'circle-stroke-opacity': ['case', dim, 0.2, 1],
      'circle-stroke-width': ['case', selected, 3, stale, 2.5, 1.5],
      'circle-emissive-strength': 1,
    },
  }
}

export function removeAthleteOverlay(map: MapboxMap) {
  stopGlide(map)
  if (map.getLayer(ATHLETE_LAYER_ID)) map.removeLayer(ATHLETE_LAYER_ID)
  if (map.getSource(ATHLETE_SOURCE_ID)) map.removeSource(ATHLETE_SOURCE_ID)
}

/**
 * The points each athlete source holds, or is gliding to. CourseMap syncs on every idle, and setting a source's data
 * again, even the same points, makes Mapbox redraw, which ends in another idle: the map would never stop drawing (as
 * with the route overlay). A glide's target counts as held, so those idles never cut it short.
 */
const sourcePoints = new WeakMap<object, AthleteOverlayData>()

/** How long a dot takes to glide to its new fix. Short of the 10 s between reads, so it lands before the next one. */
export const GLIDE_MS = 1200
/** A glide redraws at most this often: plenty for dots that move a few pixels, and light on thousands. */
const FRAME_MS = 33

type Position = [longitude: number, latitude: number]

/** Where each map draws each athlete right now, part way through a glide included. */
const drawnAt = new WeakMap<MapboxMap, Map<string, Position>>()
/** Each map's glide under way. */
const glides = new WeakMap<MapboxMap, { frame: number }>()

const positionsOf = (data: AthleteOverlayData) =>
  new Map(data.features.map((feature) => [feature.properties.id, feature.geometry.coordinates as Position]))

/** Eases out: the dot sets off at once and settles onto its fix. */
const easeOut = (t: number) => 1 - (1 - t) ** 3

function stopGlide(map: MapboxMap) {
  const glide = glides.get(map)
  if (glide) cancelAnimationFrame(glide.frame)
  glides.delete(map)
}

/** Draws `data` on the source at once. */
function draw(map: MapboxMap, source: GeoJSONSource, data: AthleteOverlayData) {
  stopGlide(map)
  source.setData(data)
  drawnAt.set(map, positionsOf(data))
}

/**
 * Moves each dot from where it is drawn to its place in `target` over GLIDE_MS; a new dot appears where it is, and a
 * dot in `still` goes straight there.
 */
function glide(map: MapboxMap, source: GeoJSONSource, target: AthleteOverlayData, from: Map<string, Position>, still: ReadonlySet<string>) {
  stopGlide(map)
  const start = performance.now()
  let drawn = -Infinity
  const state = { frame: 0 }
  const step = (now: number) => {
    // A frame is timed from when it began, which can be just before the glide did.
    const t = Math.min(1, Math.max(0, (now - start) / GLIDE_MS))
    if (t < 1 && now - drawn < FRAME_MS) {
      state.frame = requestAnimationFrame(step)
      return
    }
    drawn = now
    const k = easeOut(t)
    const frame: AthleteOverlayData =
      t < 1
        ? {
            type: 'FeatureCollection',
            features: target.features.map((feature) => {
              const was = from.get(feature.properties.id)
              if (!was || still.has(feature.properties.id)) return feature
              const [longitude, latitude] = feature.geometry.coordinates
              const coordinates: Position = [was[0] + (longitude - was[0]) * k, was[1] + (latitude - was[1]) * k]
              return { ...feature, geometry: { type: 'Point', coordinates } }
            }),
          }
        : target
    try {
      source.setData(frame)
    } catch {
      // The style changed under the glide; the next sync draws the target afresh.
      glides.delete(map)
      return
    }
    drawnAt.set(map, positionsOf(frame))
    if (t < 1) state.frame = requestAnimationFrame(step)
    else glides.delete(map)
  }
  state.frame = requestAnimationFrame(step)
  glides.set(map, state)
}

/** Whether any dot in `data` stands somewhere other than where it is drawn. */
function moved(drawn: Map<string, Position> | undefined, data: AthleteOverlayData): boolean {
  return data.features.some((feature) => {
    const was = drawn?.get(feature.properties.id)
    const [longitude, latitude] = feature.geometry.coordinates
    return was !== undefined && (was[0] !== longitude || was[1] !== latitude)
  })
}

const NO_IDS: ReadonlySet<string> = new Set()

/**
 * Draws the athletes, or takes them away when there are none. False while the map can't take them yet. With `glide`,
 * dots already on the map glide to their new fixes (GLIDE_MS); without it (reduced motion), they jump, a glide under
 * way included. `still` names the dots that jump all the same: those with a card or name label over them, which stand
 * at the newest fix.
 */
export function syncAthleteOverlay(
  map: MapboxMap,
  data: AthleteOverlayData | null | undefined,
  { glide: smooth = false, still = NO_IDS }: { glide?: boolean; still?: ReadonlySet<string> } = {},
): boolean {
  try {
    if (!data || data.features.length === 0) {
      stopGlide(map)
      drawnAt.delete(map)
      removeAthleteOverlay(map)
      return false
    }

    const source = map.getSource(ATHLETE_SOURCE_ID) as GeoJSONSource | undefined
    if (!source) {
      stopGlide(map)
      map.addSource(ATHLETE_SOURCE_ID, { type: 'geojson', data })
      drawnAt.set(map, positionsOf(data))
    } else if (sourcePoints.get(source) !== data || (!smooth && glides.has(map))) {
      const drawn = drawnAt.get(map)
      if (smooth && moved(drawn, data)) glide(map, source, data, drawn!, still)
      else draw(map, source, data)
    }
    const synced = map.getSource(ATHLETE_SOURCE_ID)
    if (synced) sourcePoints.set(synced, data)

    if (!map.getLayer(ATHLETE_LAYER_ID)) map.addLayer(athleteOverlayLayerSpec())
    return true
  } catch {
    return false
  }
}

/** How far from a dot a tap still picks it, in pixels: dots are small, fingers aren't. */
const TAP_SLOP = 12

/** The athlete whose dot is nearest `point`, within TAP_SLOP, if any. */
export function athleteAt(map: MapboxMap, point: { x: number; y: number }): string | null {
  if (!map.getLayer(ATHLETE_LAYER_ID)) return null
  const box: [[number, number], [number, number]] = [
    [point.x - TAP_SLOP, point.y - TAP_SLOP],
    [point.x + TAP_SLOP, point.y + TAP_SLOP],
  ]
  let nearest: { id: string; distance: number } | null = null
  for (const feature of map.queryRenderedFeatures(box, { layers: [ATHLETE_LAYER_ID] })) {
    const id = feature.properties?.id
    if (typeof id !== 'string' || feature.geometry.type !== 'Point') continue
    const at = map.project(feature.geometry.coordinates as [number, number])
    const distance = Math.hypot(at.x - point.x, at.y - point.y)
    if (!nearest || distance < nearest.distance) nearest = { id, distance }
  }
  return nearest?.id ?? null
}
